var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, makeObservable } from 'mobx';
import { child, primitive } from '../../utils/serializr/serializr';
import { Actor } from '../actor/Actor';
import { QuestionInfo } from './modules/QuestionInfo';
import { memoize } from '../../utils/mobx/decorators/memoize';
import { QuestionStore } from './modules/QuestionStore';
import { KnowledgeLevel } from '../learnware/LearnwareEnums';
import { AnsweringFeedback } from './modules/AnsweringFeedback';
import { alias } from '../../utils/serializr/schema/middle/alias';
import { QuestionAppear } from './modules/QuestionAppear';
import { json } from '../../utils/serializr/schema/inner/json';
import { AnsweringState } from './modules/AnsweringState';
import { QuestionScore } from './modules/QuestionScore';
import { AnsweringInfo } from './modules/AnsweringInfo';
import { runtime } from '../runtime/Runtime';
import { withInit } from '../../utils/serializr/schema/middle/withInit';
import { editable } from '../../utils/mobx/decorators/editable';
import { QuestionLayout } from './modules/QuestionLayout';
import { QuestionStrip } from './modules/QuestionStrip';
import { strip } from '../../utils/serializr/schema/inner/strip';
import { QuestionRating } from './modules/QuestionRating';
import { UTCdate } from '../../utils/serializr/schema/inner/UTCdate';
import { QuestionComments } from './modules/QuestionComments';
import { QuestionScoringStore } from './scoring/QuestionScoringStore';
import { uniq } from '../../utils/helper/lodash_replacements';
import { AnsweringStepMistake } from './modules/AnsweringStepMistake';
import { AnsweringStepCorrect } from './modules/AnsweringStepCorrect';
import { QuestionPrepare } from './modules/QuestionPrepare';
import { PersistActor } from '../actor/PersistActor';
export class Question extends PersistActor {
    type = 'question';
    constructor(init) {
        super(init);
        makeObservable(this);
    }
    get importingChildren() {
        return super.importingChildren.concat(this.topic);
    }
    get exportingChildren() {
        return [...super.exportingChildren, this.questionPart];
    }
    get id() {
        return this.id_raw;
    }
    set id(id) {
        this.id_raw = id;
    }
    topic_id = 0;
    subtopic_id = 0;
    subtopicName_stored = '';
    layout;
    lang = 'de';
    accepted = 1; // QuestionAcceptedState.Online;
    creator_id = 0;
    editor_id = 0;
    lastEdit;
    screenshot = 0;
    copy_of = 0;
    count = 1;
    premium = 0;
    successionIndex = 0; // unused in question, use in micro-question (not up to date)
    mobileCount = 1;
    ratingCount = 1;
    floatCount = 1;
    floatMobileCount = 1;
    floatBlocked = 1;
    floatSkipped = 1;
    floatMobileSkipped = 1;
    floatPassed = 1;
    floatReached = 1;
    floatReachable = 1;
    floatRating = 1;
    floatRateable = 1;
    answerSeconds = 1;
    exitRate = 5; // percentage of users not answering this question
    skipRate = 5; // percentage of users skipping this question
    assetSize = 0;
    hasMeasuredAssetSize = false;
    measuredAssetSize = 0;
    cpuCost = 0;
    shuffle = 1;
    app_hide = 0;
    metainfo;
    inTopic;
    scoringStore;
    level = KnowledgeLevel.Abfragen;
    levelTag = '';
    questionStore;
    isShuffableQuestionType = true;
    get isCurrentEditQuestion() {
        if (!this.isWriteEnabled)
            return false;
        if (this.context.app.question !== this)
            return false;
        return this.actorBundles.areAllActiveBundlesAvailable;
    }
    get questionPart() {
        return this.questionStore.questionPart;
    }
    get micro() {
        return this.context.solidTopic.topicMicroQuestions.microQuestions.find(micro => micro.id === this.id);
    }
    get mini() {
        return this.context.solidTopic.topicMiniQuestions.miniQuestions.find(mini => mini.id === this.id);
    }
    get subtopicName() {
        return this.subtopicName_stored;
    }
    set subtopicName(subtopic) {
        this.subtopicName_stored = subtopic;
        if (this.micro) {
            this.micro.subtopicName = subtopic;
        }
        this.context.app.solidTopic.topicMicroQuestions.microQuestionsVersion++;
    }
    get questionEdit() {
        return new runtime.solidPackages.Edit.classes.QuestionEdit(this);
    }
    get questionInfo() {
        return new QuestionInfo(this);
    }
    get questionStrip() {
        return new QuestionStrip(this);
    }
    get answeringState() {
        return new AnsweringState(this);
    }
    get answeringInfo() {
        return new AnsweringInfo(this);
    }
    get answeringFeedback() {
        return new AnsweringFeedback(this);
    }
    get answeringStepMistake() {
        return new AnsweringStepMistake(this);
    }
    get answeringStepCorrect() {
        return new AnsweringStepCorrect(this);
    }
    get questionAppear() {
        return new QuestionAppear(this);
    }
    get questionComments() {
        return new QuestionComments(this);
    }
    get questionLayout() {
        return new QuestionLayout(this);
    }
    get questionRating() {
        return new QuestionRating(this);
    }
    get questionScore() {
        return new QuestionScore(this);
    }
    get questionPrepare() {
        return new QuestionPrepare(this);
    }
    get topic() {
        if (this.context.app) {
            return this.context.app.model.stream.topic[this.topic_id];
        }
        return runtime.model.stream.topic[this.topic_id];
    }
    get solidTopic() {
        return this.topic;
    }
    get subtopic() {
        return this.context.app.solidTopic.topicSubtopics.subtopicByName(this.subtopicName);
    }
    get answer() {
        return this.parent.type === 'answer' ? this.parent : this.questionEdit.editAnswer;
    }
    get solidAnswer() {
        return this.answer;
    }
    layoutName_fallback;
    get layoutName() {
        return this.questionLayout.layoutName || this.layoutName_fallback;
    }
    set layoutName(l) {
        this.layoutName_fallback = l;
    }
    get appJson() {
        return this.questionStrip.stripped;
    }
    set appJson(j) {
        this.questionStrip.stripped = j;
    }
    get bundlesAuto() {
        if (this.actorBundles.areAllActiveBundlesAvailable) {
            return uniq([...this.actorBundles.deepPreloadBundleIds, ...this.context.app.actorBundles.deepPreloadBundleIds]).join(',');
        }
        return '';
    }
    bundles_fallback = '';
    get bundles() {
        return this.bundlesAuto || this.bundles_fallback;
    }
    set bundles(t) {
        this.bundles_fallback = t;
    }
    get bundleNamesAuto() {
        if (this.actorBundles.areAllActiveBundlesAvailable) {
            return uniq([...this.actorBundles.deepPreloadBundleNames, ...this.context.app.actorBundles.deepPreloadBundleNames]).join(',');
        }
        return '';
    }
    bundlesNames_fallback;
    get bundleNames() {
        return this.bundleNamesAuto || this.bundlesNames_fallback;
    }
    set bundleNames(t) {
        this.bundlesNames_fallback = t;
    }
    /* Mappings to main columns */
    bundleSize_fallback;
    get bundleSize() {
        if (this.questionLayout.layoutBundle) {
            return this.questionLayout.layoutBundle.assetSize || this.bundleSize_fallback;
        }
        return this.bundleSize_fallback;
    }
    set bundleSize(t) {
        this.bundleSize_fallback = t;
    }
    appSignals = '';
    get shuffleSeed() {
        if (!this.answer || !this.answer.id)
            return this.id_string;
        return this.answer.id;
    }
    get maxPoints() {
        return this.questionInfo.maxPoints;
    }
    get shouldShuffle() {
        if (this.context.app.appInfo.isUserCurrentlyEditing)
            return false;
        return runtime.isBrowserRuntime && this.isShuffableQuestionType && !!this.shuffle;
    }
    textBasis_cached;
    get textBasis() {
        if (this.questionPart && this.questionPart.inner.local.has.text) {
            return this.questionPart.inner.aspectContent.regional.allEnabledTextBasisStringsJoined;
        }
        return '';
        if (this.isCurrentEditQuestion && this.questionPart && this.questionPart.inner.local.has.text) {
            const value = this.questionPart.inner.aspectContent.regional.allEnabledTextBasisStringsJoined;
            this.textBasis_cached = value;
            return value;
        }
        return this.textBasis_cached;
    }
    set textBasis(text) {
        this.textBasis_cached = text;
        if (text.length === 0 &&
            runtime.runtimePermissions.hasEditFunctionality &&
            this.questionPart &&
            this.questionPart.inner.local.has.text) {
            this.questionPart.inner.local.traitOf.text.traitEdit.remove();
            console.log('REMOVE TEXT');
            return;
        }
        if (this.questionPart.inner.local.traitsOf.text.length > 1) {
            // split texts
            return;
        }
        if (runtime.runtimePermissions.hasEditFunctionality && this.questionPart && !this.questionPart.inner.local.has.text) {
            this.questionPart.actorContent.local.firstTextBasis = text;
        }
    }
    static get serializeInfo() {
        return {
            extends: Actor.serializeInfo,
            props: {
                count: true,
                topic_id: true,
                subtopic_id: withInit(primitive, 0),
                answerSeconds: true,
                exitRate: true,
                skipRate: true,
                assetSize: true,
                bundleSize: true,
                cpuCost: true,
                rating: true,
                floatRating: true,
                floatRateable: true,
                floatSkipped: true,
                floatBlocked: true,
                floatCount: true,
                floatReached: true,
                floatReachable: true,
                floatPassed: true,
                subtopicName_stored: alias('tag', primitive),
                bundles: true,
                bundleNames: true,
                appSignals: true,
                ratingCount: true,
                layout: withInit(primitive, context => {
                    const qt = context.objectContext.ser.questionType;
                    if (qt === 'KnowAWord')
                        return 'GnIuCTymP';
                    if (qt === 'CorrectWrong')
                        return '1A3fRORyn';
                    if (qt === 'MultipleChoice')
                        return 'fWKATDmIk';
                    if (qt === 'Associate')
                        return 'pmYYu4JCU';
                    if (qt === 'MemorizePoints')
                        return 'O-MjCajpK';
                    if (qt === 'ThinkAndReveal')
                        return 'Gz_txPsDZ';
                    return '';
                }, ['']),
                layoutName: withInit(primitive, ''),
                accepted: true,
                premium: true,
                shuffle: true,
                app_hide: true,
                creator_id: true,
                successionIndex: true,
                editor_id: true,
                screenshot: true,
                lastEdit: UTCdate(),
                metainfo: alias('json_metainfo', withInit(json(), {})),
                inTopic: withInit(json(), {}),
                questionStore: withInit(child(QuestionStore), {}),
                appJson: withInit(json(), {}),
                scoringStore: withInit(child(QuestionScoringStore), {}),
                level: true,
                levelTag: true,
            },
            afterDeserialize: async ({ target, context }) => {
                if (runtime.runtimePermissions.hasEditFunctionality) {
                    const question = target;
                    if (!question.hasMeasuredAssetSize) {
                        runtime.runtimeScheduler.addToNextIdleStateChange(() => {
                            question.assetSize = question.measuredAssetSize;
                        });
                    }
                    question.hasMeasuredAssetSize = true;
                }
            },
        };
    }
    static get stripInfo() {
        return {
            extends: Actor.stripInfo,
            props: {
                questionPart: strip((q) => q.questionPart.stripped),
            },
        };
    }
}
__decorate([
    editable()
], Question.prototype, "topic_id", void 0);
__decorate([
    editable()
], Question.prototype, "subtopic_id", void 0);
__decorate([
    editable()
], Question.prototype, "subtopicName_stored", void 0);
__decorate([
    editable()
], Question.prototype, "layout", void 0);
__decorate([
    editable()
], Question.prototype, "lang", void 0);
__decorate([
    editable()
], Question.prototype, "accepted", void 0);
__decorate([
    editable()
], Question.prototype, "creator_id", void 0);
__decorate([
    editable()
], Question.prototype, "editor_id", void 0);
__decorate([
    editable()
], Question.prototype, "lastEdit", void 0);
__decorate([
    editable()
], Question.prototype, "copy_of", void 0);
__decorate([
    editable()
], Question.prototype, "count", void 0);
__decorate([
    editable()
], Question.prototype, "premium", void 0);
__decorate([
    editable()
], Question.prototype, "mobileCount", void 0);
__decorate([
    editable()
], Question.prototype, "floatRating", void 0);
__decorate([
    editable()
], Question.prototype, "floatRateable", void 0);
__decorate([
    editable()
], Question.prototype, "assetSize", void 0);
__decorate([
    editable()
], Question.prototype, "cpuCost", void 0);
__decorate([
    editable()
], Question.prototype, "shuffle", void 0);
__decorate([
    editable()
], Question.prototype, "app_hide", void 0);
__decorate([
    editable('ref')
], Question.prototype, "metainfo", void 0);
__decorate([
    editable('ref')
], Question.prototype, "inTopic", void 0);
__decorate([
    editable('ref')
], Question.prototype, "scoringStore", void 0);
__decorate([
    editable()
], Question.prototype, "level", void 0);
__decorate([
    editable()
], Question.prototype, "levelTag", void 0);
__decorate([
    memoize
], Question.prototype, "micro", null);
__decorate([
    computed
], Question.prototype, "subtopicName", null);
__decorate([
    memoize
], Question.prototype, "questionEdit", null);
__decorate([
    memoize
], Question.prototype, "questionInfo", null);
__decorate([
    memoize
], Question.prototype, "questionStrip", null);
__decorate([
    memoize
], Question.prototype, "answeringState", null);
__decorate([
    memoize
], Question.prototype, "answeringInfo", null);
__decorate([
    memoize
], Question.prototype, "answeringFeedback", null);
__decorate([
    memoize
], Question.prototype, "answeringStepMistake", null);
__decorate([
    memoize
], Question.prototype, "answeringStepCorrect", null);
__decorate([
    memoize
], Question.prototype, "questionAppear", null);
__decorate([
    memoize
], Question.prototype, "questionComments", null);
__decorate([
    memoize
], Question.prototype, "questionLayout", null);
__decorate([
    memoize
], Question.prototype, "questionRating", null);
__decorate([
    memoize
], Question.prototype, "questionScore", null);
__decorate([
    memoize
], Question.prototype, "questionPrepare", null);
__decorate([
    computed
], Question.prototype, "topic", null);
__decorate([
    editable()
], Question.prototype, "appSignals", void 0);
__decorate([
    computed
], Question.prototype, "textBasis", null);
__decorate([
    memoize
], Question, "serializeInfo", null);
__decorate([
    memoize
], Question, "stripInfo", null);
